// TeamRole is used by the integration-definition / scope / HITL types folded in
// from the former types/plugin.ts (ENG-7168).
import type { TeamRole } from './team.js';

export type IntegrationScope = 'organization' | 'team' | 'agent';

export type IntegrationStatus = 'pending' | 'configured' | 'active' | 'error' | 'revoked';

export type IntegrationAuthType = 'oauth2' | 'api_key' | 'webhook' | 'managed' | 'none';

export type IntegrationId = 'linear' | 'github' | 'google-workspace' | 'gcloud' | 'xero' | 'granola' | 'brand-ninja' | 'kajabi' | 'postiz' | 'higgsfield' | 'qmd' | 'v0' | 'pika' | 'claude-code' | 'xurl' | 'coderabbit' | 'aws' | 'anchor-browser' | 'deck' | 'elevenlabs' | 'firecrawl' | 'augmented-admin' | 'augmented-support' | 'greenlight' | 'expo' | 'custom';

export type IntegrationCategory =
  | 'project-management'
  | 'code'
  | 'accounting'
  | 'crm'
  | 'communication'
  | 'storage'
  | 'workspace-productivity'
  | 'knowledge'
  | 'ui-generation'
  | 'media'
  | 'social'
  | 'infrastructure'
  | 'custom';

export interface IntegrationCapability {
  id: string;
  name: string;
  description: string;
  access: 'read' | 'write' | 'admin';
  required_scopes?: string[];
}

export interface IntegrationCliTool {
  package: string;
  /** Binary name on PATH (e.g. 'linear', 'gh') — used by `command -v` to decide whether to install. */
  binary: string;
  env_key: string;
  skill_id?: string;
  /** Additional env vars to set alongside the API key */
  extra_env?: Record<string, string>;
  /**
   * How the manager should install the CLI when it's missing from the host.
   *   - 'npm': global install via `npm install -g <package>`
   *   - 'brew': install via `brew install <package>` (macOS, and Linuxbrew when present)
   *   - 'script': run `script` verbatim (whitelisted URL; the catalog is the trust boundary)
   *   - 'manual': do not auto-install — log a hint; operator handles it out of band.
   * Omit to default to 'manual' for backward compatibility.
   */
  installer?: 'npm' | 'brew' | 'script' | 'manual';
  /** Only used when installer === 'script'. Must be a single shell command. */
  script?: string;
}

/**
 * ENG-5855: declarative spec for a custom-header (non-OAuth) HOSTED remote
 * MCP, declared on an `IntegrationDefinition.remoteMcp` field. The claudecode
 * adapter renders it into `.mcp.json` via `buildRemoteMcpEntry`.
 *
 * Unlike the OAuth path (fixed `Authorization: Bearer ${ID_ACCESS_TOKEN}`),
 * the header names and values are arbitrary — the manager substitutes each
 * `${VAR}` from `.env.integrations` at MCP-spawn time. For Anchor Browser:
 *   headers: {
 *     'anchor-api-key':   '${ANCHOR_BROWSER_API_KEY}',   // from api_key cred
 *     'anchor-session-id':'${ANCHOR_BROWSER_SESSION_ID}', // minted by ENG-5857
 *   }
 */
/**
 * ENG-6993 / ADR-0033: structured auth for a hosted remote MCP. The runtime
 * renders the credential into the request header — there is deliberately NO
 * free-form `${VAR}` string the operator/catalog can set.
 *
 * SECURITY (ADR-0033 C1 — closes the credential-exfiltration surface): the
 * env var is DERIVED by `buildRemoteMcpEntry` from the integration's OWN
 * `definition_id` + `credential_ref` (`<DEFINITION_ID>_<CREDENTIAL_REF>`, e.g.
 * `anchor-browser` + `api_key` → `ANCHOR_BROWSER_API_KEY`). Because the name is
 * derived from the integration's own id, a catalog row can never reference a
 * DIFFERENT integration's / customer's secret — the confused-deputy template
 * injection of the rejected `headers:{Authorization:"Bearer ${ANY_VAR}"}` shape
 * is impossible by construction.
 */
export interface RemoteMcpAuth {
  /**
   * How to render the credential:
   *   - 'bearer' → `Authorization: Bearer <token>`
   *   - 'header' → `<header_name>: <token>` (custom header, e.g. Anchor's
   *     `anchor-api-key`). `header_name` is required for this scheme.
   */
  scheme: 'bearer' | 'header';
  /** Required when `scheme === 'header'` — the custom header name. */
  header_name?: string;
  /**
   * The credential FIELD name on this integration (e.g. `api_key`). The env
   * var is derived as `<DEFINITION_ID>_<CREDENTIAL_REF>` — never a free string.
   */
  credential_ref: string;
}

export interface RemoteMcpSpec {
  /** Transport — defaults to 'http' (streamable HTTP) when omitted. */
  type?: 'http' | 'sse';
  /** The hosted MCP endpoint. */
  url: string;
  /**
   * ENG-6993 / ADR-0033: structured auth (preferred). When present,
   * `buildRemoteMcpEntry` renders the credential header from this — the env var
   * is derived from this integration's own id, so it cannot reference another
   * integration's secret. New hosted-remote-MCP integrations (monday.com, and
   * Anchor post-migration) use `auth`; the legacy free-form `headers` below is
   * retained only for non-credential / dynamic headers (e.g. Anchor's
   * minted `anchor-session-id`) and is being phased out for credential headers.
   */
  auth?: RemoteMcpAuth;
  /**
   * Headers sent with each request. `${VAR}` values are resolved at
   * spawn time from `.env.integrations` (credential-derived vars like
   * `<ID>_API_KEY`, or vars another ticket populates).
   *
   * NOTE (ADR-0033): for CREDENTIAL headers prefer `auth` above — it scopes the
   * env var to this integration. `headers` remains for non-secret / dynamically
   * minted headers (e.g. `anchor-session-id`). A `headers` entry whose value
   * references a `${VAR}` not derivable from THIS integration is a smell.
   */
  headers?: Record<string, string>;
  /**
   * Env vars to seed in `.env.integrations` with a default value when the
   * integration is present but nothing else has written them yet. Prevents
   * a referenced-but-unset `${VAR}` from shipping as a literal placeholder
   * (which would corrupt the header). Anchor seeds `ANCHOR_BROWSER_SESSION_ID`
   * to '' so stateless browsing works until ENG-5857 mints a real session.
   * A later writer (real credential / config / session mint) overrides it.
   */
  envDefaults?: Record<string, string>;
}

/**
 * ENG-6920: how a premium (billable) integration is priced. `monthly` = a flat
 * subscription; `usage` = metered per unit of consumption (e.g. Deck compute
 * time + agent runs). The actual amounts are NOT here — they live with the
 * billing mechanism (Stripe), which is deferred. This only declares the model
 * so the catalog, entitlement and metering slices can branch on it.
 */
export type PremiumPricingMethodology = 'monthly' | 'usage';

/**
 * ENG-7032: a billable meter a premium integration emits. The CODE declares
 * WHAT is metered and in WHICH physical unit; the priced rate card
 * (integration_rate_cards) holds the per-unit price for each (event_type,
 * currency). `event_type` must match the value the integration's broker writes
 * to `integration_usage_events.event_type` (e.g. Deck's 'run_task'), so usage
 * rows can be joined to a rate.
 */
export interface PremiumMeter {
  /** The metered event the broker emits, e.g. 'run_task'. */
  event_type: string;
  /** The physical unit one metered unit represents, e.g. 'run', 'character'. */
  unit: string;
}

export interface PremiumDescriptor {
  /** Pricing methodology for this premium integration. */
  pricing: PremiumPricingMethodology;
  /**
   * Optional human-readable pricing note for the UI, e.g.
   * "Billed on Deck compute time and agent runs." Not a machine price.
   */
  note?: string;
  /**
   * ENG-7032: the billable meters this integration emits (usage-priced
   * integrations). Each declares an `event_type` + physical `unit`; the rate
   * card prices them per currency. Empty/absent for a monthly-priced premium
   * that meters nothing per operation.
   */
  meters?: PremiumMeter[];
}

/**
 * ENG-7015: picker-facing metadata for a customer-installable NATIVE
 * integration. When an `IntegrationDefinition` carries this, the Add Integration
 * picker offers it directly (not via the Composio DB catalog) and the org
 * allowlist must therefore govern it. It is the SINGLE source of truth that
 * replaces the two hand-aligned native lists (`STATIC_INTEGRATION_OPTIONS` in
 * the webapp + `NATIVE_PICKER_INTEGRATIONS` in the API), which used to drift.
 *
 * It carries the picker-facing display fields rather than reusing the
 * definition's own `category` / `supported_auth_types`, because those serve a
 * different purpose:
 *   - `category` here is the DISPLAY label the dialog groups by (e.g. "Code"),
 *     not the `IntegrationCategory` slug ("code") the runtime uses.
 *   - `authTypes` here is the set of auth options the CONNECT UI offers, which
 *     can differ from `supported_auth_types` (the runtime capability set) — e.g.
 *     xurl offers a keyless "none" option in the picker that the runtime spec
 *     omits, and GitHub's picker order is OAuth-first.
 */
export interface InstallablePickerMeta {
  /** Display category label shown in the Add Integration picker + org allowlist. */
  category: string;
  /** Auth options the connect UI offers (may differ from supported_auth_types). */
  authTypes: IntegrationAuthType[];
}

export interface IntegrationDefinition {
  id: IntegrationId;
  name: string;
  category: IntegrationCategory;
  description: string;
  supported_auth_types: IntegrationAuthType[];
  capabilities: IntegrationCapability[];
  config_schema?: object;
  icon?: string;
  docs_url?: string;
  cli_tool?: IntegrationCliTool;
  /**
   * ENG-7015: present when this integration is a customer-installable native
   * that the Add Integration picker offers directly. Carries the picker-facing
   * display metadata (see {@link InstallablePickerMeta}). Both the picker and
   * the org allowlist derive their curated native list from the entries that
   * set this, so there is exactly one source for "which natives are
   * installable". Absent for Composio-catalog integrations (DB-driven), runtime
   * frameworks (claude-code, v0), dev CLIs (coderabbit) and internal staff-only
   * tools (augmented-admin) — none of which are customer-installable.
   */
  installable?: InstallablePickerMeta;
  /** Marks the integration as experimental — UI shows a "Beta" badge */
  beta?: boolean;
  /**
   * ENG-6920: marks an integration as PREMIUM (billable). Absent => free: it
   * authenticates with the customer's own account (Linear, GitHub, the
   * Composio/OAuth set), so upstream cost lands on the customer's bill and
   * there is nothing for Augmented to meter. A premium integration is one
   * Augmented pays for centrally (e.g. Deck's single account key), so it is
   * gated on a per-org opt-in entitlement and its usage is metered.
   *
   * This descriptor is the catalog foundation only — the entitlement model,
   * the enable-gate, usage metering and budget caps are separate slices that
   * READ it. It carries no machine price; actual amounts live with the billing
   * mechanism (Stripe), which is deferred.
   */
  premium?: PremiumDescriptor;
  /**
   * ENG-5815: data-driven native (stdio) MCP server entry. When set,
   * the claudecode framework adapter emits this entry into `.mcp.json`
   * via the templated renderer in `provisioning/native-mcp.ts`, no
   * core code change required to add a new integration.
   *
   * Integrations with conditional rendering (broker-mode toggles,
   * config-derived env, etc.) still need a hand-rolled handler — leave
   * `nativeMcp` undefined for those and keep the if-block. See
   * `claudecode/index.ts:buildMcpJson` for the migration boundary.
   */
  nativeMcp?: import('../provisioning/native-mcp.js').NativeMcpSpec;
  /**
   * ENG-5855: data-driven HOSTED remote (streamable-HTTP / SSE) MCP server
   * entry with custom, non-OAuth header auth. When set, the claudecode
   * adapter emits this entry into `.mcp.json` via `buildRemoteMcpEntry`,
   * no core code change required to add a new integration.
   *
   * This is the api-key-header sibling of the OAuth `mcpUrl` path: instead
   * of a fixed `Authorization: Bearer ${ID_ACCESS_TOKEN}` header, the spec
   * carries an arbitrary templated headers map (e.g. Anchor Browser's
   * `anchor-api-key` + dynamic `anchor-session-id`). Leave undefined for
   * OAuth remote MCPs — those stay on the `OAUTH_PROVIDERS.mcpUrl` path.
   */
  remoteMcp?: RemoteMcpSpec;
}

export interface IntegrationCredentials {
  api_key?: string;
  access_token?: string;
  refresh_token?: string;
  /** ISO timestamp when the access_token expires */
  token_expires_at?: string;
  [key: string]: unknown;
}

export interface Integration {
  id: string;
  scope: IntegrationScope;

  organization_id?: string;
  team_id?: string;
  agent_id?: string;

  definition_id: string;
  display_name: string;

  auth_type: IntegrationAuthType;
  credentials: IntegrationCredentials;
  config: Record<string, unknown>;

  status: IntegrationStatus;
  status_message?: string;

  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ResolvedIntegration {
  /**
   * ENG-4920: integration row UUID. Surfaced through to provisioning so
   * vendor MCP servers (xero-mcp-server, …) can be wired with
   * `AGT_INTEGRATION_ID` and call the per-call credential broker
   * endpoint (POST /host/agent-integrations/:id/credential) instead of
   * baking the access_token into spawn-time env. Optional for
   * back-compat with code paths that build a ResolvedIntegration
   * without going through /host/agent-integrations (e.g. tests).
   */
  id?: string;
  definition_id: string;
  display_name: string;
  scope: IntegrationScope;
  auth_type: IntegrationAuthType;
  credentials: IntegrationCredentials;
  config: Record<string, unknown>;
  capabilities: IntegrationCapability[];
  /**
   * ENG-6993 / ADR-0033 (Slice 2): the hosted-remote-MCP descriptor sourced
   * from the integration's `integration_definitions.remote_mcp` catalog column.
   * When present, provisioning renders the `.mcp.json` entry from THIS spec
   * (the DB catalog row is the source of truth) instead of looking the spec up
   * in the code `INTEGRATION_REGISTRY`. Absent for non-remote-MCP integrations,
   * and for callers not yet wired to forward the column — those fall back to the
   * code registry, so the output is unchanged (Anchor stays byte-identical).
   * Retires the code/DB duality the ADR calls out.
   */
  remoteMcp?: RemoteMcpSpec;
  /**
   * ENG-7358: catalog cutover flag for integrations served by a DEDICATED
   * LOCAL stdio MCP server bundled with the CLI (`~/.augmented/_mcp/<id>.js`).
   * Sourced from `integration_definitions.metadata.stdio_mcp` and forwarded
   * through `/host/agent-integrations` (snake_case `stdio_mcp`, mapped by the
   * manager alongside `remote_mcp` -> `remoteMcp`). When true, the claudecode
   * adapter emits the integration's own `.mcp.json` server entry; when
   * false/absent nothing changes, so the code path ships dark and cutover +
   * rollback are pure catalog operations. Origami is the first user.
   */
  stdioMcp?: boolean;
}

// ---------------------------------------------------------------------------
// ENG-7168: folded in from the former types/plugin.ts (integration-domain
// types kept under the legacy 'plugin' filename during the ENG-6009 rename).
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// IntegrationDef Skills
// ---------------------------------------------------------------------------

export interface IntegrationDefSkill {
  id: string;
  name: string;
  content: string;
  references: Array<{ url: string; label?: string }>;
  /** Which scopes this skill covers. undefined/empty = all scopes (legacy). */
  scope_ids?: string[];
}

// ---------------------------------------------------------------------------
// IntegrationDef Scripts / Hooks
// ---------------------------------------------------------------------------

export interface IntegrationDefScripts {
  on_install?: string;
  on_uninstall?: string;
  on_upgrade?: string;
  on_connect?: string;
}

// ---------------------------------------------------------------------------
// IntegrationDef Permission Scopes
// ---------------------------------------------------------------------------

/**
 * Canonical HITL tier order (ENG-5123 / ADR 0004). Strictness increases
 * left-to-right: read < write < write_high_risk < write_destructive < admin.
 * The HITL resolver maps tier strings to this index and picks the highest.
 *
 * NOTE: `@augmented/approval-core` declares the same union as
 * `ApprovalRiskTier`. Duplicated here (with shared values) to avoid a
 * core→approval-core dependency cycle. Unifying behind a shared package is a
 * follow-up; both unions must stay in lockstep until then.
 */
export const HITL_TIER_ORDER = [
  'read',
  'write',
  'write_high_risk',
  'write_destructive',
  'admin',
] as const;

export type HitlTier = (typeof HITL_TIER_ORDER)[number];

/** Canonical ordinal — higher = stricter. Use for comparisons, never lexical. */
export const HITL_TIER_RANK: Readonly<Record<HitlTier, number>> = Object.freeze(
  Object.fromEntries(HITL_TIER_ORDER.map((tier, i) => [tier, i])) as Record<
    HitlTier,
    number
  >,
);

/**
 * Install-time / definition-time per-tool override. Always RAISES the tier
 * (never lowers). Validators reject any `raised_to` whose rank is
 * less-than-or-equal to the catalog floor for `tool_key`.
 */
export interface ToolHitlOverride {
  /** Provider-native tool identifier — must match `tool_definitions.tool_key`. */
  tool_key: string;
  /** New ceiling for this tool. MUST raise per HITL_TIER_RANK. */
  raised_to: HitlTier;
  /** Required prose explaining why this override exists. */
  justification: string;
}

/**
 * Per-install approver routing override stored on
 * `agent_integrations.approver_route`. Discriminated by `kind`:
 *  - `channel`: route to a shared channel from the channel registry. The
 *    channel must be installed for the team and at an appropriate security
 *    tier for the strictest verb tier (validated server-side, fail-closed).
 *  - `dm`: route to a specific user via their preferred contact channel.
 *    The user MUST be a member of the same team as the agent_integrations
 *    row AND eligible to approve at the strictest tier in the install.
 *    Channel preference is resolved against `user_channel_preferences`.
 */
export type ApproverRoute =
  | { kind: 'channel'; channel_type: string; channel_id: string }
  | { kind: 'dm'; user_id: string };

/**
 * Output of the Integration Mode interview (ENG-5129). Consumed by the
 * manifest validator and ultimately persisted as an
 * `integration_definitions` row at `scope=team, status=draft`.
 *
 * The shape mirrors `integration_definitions.defined_scopes` so the
 * persistence path is a near-straight copy. The skill (`.claude/skills/
 * integration-mode/SKILL.md`) is the affordance that gathers these
 * fields; this type is the contract the API validates.
 */
export interface IntegrationManifest {
  /** Free-text outcome the contributor articulated in step 1. */
  goal: string;
  /** Optional pre-binding to a specific agent. */
  target_agent_id?: string;
  /** Each toolkit + its enabled scopes. Multi-toolkit is allowed; composition is deferred. */
  toolkits: Array<{
    /** Matches `toolkit_definitions.id`. */
    toolkit_id: string;
    scopes: IntegrationManifestScope[];
  }>;
  /**
   * sha256 hex of the canonicalised manifest body (goal + toolkits).
   * Lets the API confirm the contributor saw the exact replay they
   * confirmed at step 5 — prevents drift between interview and persist.
   */
  confirmation_hash: string;
}

export interface IntegrationManifestScope {
  /** Unique within this manifest; conventionally `<toolkit>:<verb>`. */
  scope_id: string;
  name: string;
  description: string;
  default_min_role: TeamRole;
  /** Provider tool_keys; each MUST exist in `tool_definitions`. */
  tools: string[];
  /** RAISE-only per-tool overrides; justification required. */
  tool_overrides: ToolHitlOverride[];
}

export interface IntegrationDefScope {
  /** Unique scope identifier, e.g. 'xero:invoices:read' */
  id: string;
  /** Human-readable name, e.g. 'Read Invoices' */
  name: string;
  description: string;
  /** Which toolkit this scope belongs to */
  toolkit_id: string;
  /** Provider action slugs this scope grants access to */
  tools: string[];
  /**
   * Optional per-tool HITL raises declared at the integration_definition
   * level. Each entry strictly raises the corresponding tool's catalog floor
   * for any agent installing this integration. Validators enforce the
   * strict-raise invariant against `tool_definitions.min_hitl_tier`.
   */
  tool_overrides?: ToolHitlOverride[];
  /** IntegrationDef author's recommended minimum role to grant this scope */
  default_min_role: TeamRole;
  /**
   * OAuth provider scope strings this catalog scope requires at consent
   * time, e.g. `['payroll.employees', 'payroll.payruns']` for `xero:payroll`.
   * Used by the agent-scope-deficit calculator to detect when an installed
   * skill needs OAuth scopes the existing credential doesn't carry — refresh
   * tokens can never widen scope, so the only remediation is reconnect.
   * Omitted/empty means the scope has no OAuth-side dependency.
   */
  oauth_scopes?: string[];
}

// ---------------------------------------------------------------------------
// IntegrationDef
// ---------------------------------------------------------------------------

// ENG-7063: `alpha` sits earlier than `beta` in the maturity lifecycle. Alpha
// integrations are fully installable + usable but HIDDEN from end users by
// default — they surface only to Integrity-Labs admins who opt in via a
// "Show Alpha" toggle (admin route `?include_alpha=true` + the dialog checkbox;
// that reveal UI is the follow-up slice). Visibility matrix: non-admins see only
// `published`; admins see `beta` by default and `alpha`/`draft` behind opt-in flags.
export type IntegrationDefStatus = 'draft' | 'alpha' | 'beta' | 'published' | 'archived';

export interface IntegrationDef {
  id: string;
  organization_id: string | null;
  team_id: string | null;
  name: string;
  slug: string;
  description: string | null;
  category: string;
  icon: string | null;
  required_toolkits: string[];
  skills: IntegrationDefSkill[];
  allowed_tools: string[];
  scripts: IntegrationDefScripts;
  defined_scopes: IntegrationDefScope[];
  /**
   * Optional JSON Schema (Draft 2020-12, constrained subset) declaring the
   * typed context fields this plugin accepts. NULL means the plugin has no
   * typed context — only the universal freeform overrides field is available.
   * See ENG-4341 / docs/plugins/plugin-context-rfc.md.
   */
  context_schema: IntegrationContextSchema | null;
  version: number;
  published_at: string | null;
  status: IntegrationDefStatus;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// IntegrationDef Context (ENG-4341)
//
// User-supplied per-plugin tuning data. Two parts:
//   - `values`:    typed config validated against `IntegrationDef.context_schema`
//   - `overrides`: freeform Markdown appended verbatim to every rendered
//                  SKILL.md as a "## Team Overrides" section
// ---------------------------------------------------------------------------

/**
 * The constrained subset of JSON Schema (Draft 2020-12) that plugin authors
 * may declare for their context. The full JSON Schema spec is much larger;
 * we only support what `@vercel-labs/json-render` can render and Ajv can
 * validate without escape hatches.
 *
 * Supported field types:
 *   - string  (with optional `enum`)
 *   - boolean
 *   - array of string
 *   - flat object as additionalProperties: { type: string } (key-value map)
 *
 * Deferred (will reject in meta-schema validation):
 *   - number / integer
 *   - nested object schemas
 *   - oneOf / anyOf / $ref / format (beyond Ajv defaults)
 *   - the x-augmented-dimension extension keyword (cut from slice 1; see RFC §1b)
 */
export interface IntegrationContextSchema {
  $schema?: string;
  type: 'object';
  properties: Record<string, IntegrationContextFieldSchema>;
  required?: string[];
}

export type IntegrationContextFieldSchema =
  | IntegrationContextStringField
  | IntegrationContextBooleanField
  | IntegrationContextStringArrayField
  | IntegrationContextStringMapField;

interface IntegrationContextFieldBase {
  title?: string;
  description?: string;
}

export interface IntegrationContextStringField extends IntegrationContextFieldBase {
  type: 'string';
  enum?: string[];
  default?: string;
}

export interface IntegrationContextBooleanField extends IntegrationContextFieldBase {
  type: 'boolean';
  default?: boolean;
}

export interface IntegrationContextStringArrayField extends IntegrationContextFieldBase {
  type: 'array';
  items: { type: 'string' };
  default?: string[];
}

export interface IntegrationContextStringMapField extends IntegrationContextFieldBase {
  type: 'object';
  additionalProperties: { type: 'string' };
  default?: Record<string, string>;
}

/**
 * Concrete value types that can be stored in IntegrationContext.values, derived
 * from the schema field types above. Top-level keys correspond to property
 * names in the plugin's `context_schema.properties`.
 */
export type IntegrationContextValue =
  | string
  | boolean
  | string[]
  | Record<string, string>;

export type IntegrationContextValues = Record<string, IntegrationContextValue>;

export type IntegrationContextScope = 'organization' | 'team' | 'agent';

export interface IntegrationContext {
  id: string;
  plugin_id: string;
  scope: IntegrationContextScope;
  organization_id: string | null;
  team_id: string | null;
  agent_id: string | null;
  values: IntegrationContextValues;
  /** Freeform Markdown — never validated against context_schema. */
  overrides: string;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Pre-resolved plugin context delivered via /host/refresh. Inheritance
 * (org → team → agent) and schema defaults have already been flattened
 * server-side. The manager just consumes this and substitutes.
 */
export interface ResolvedIntegrationContext {
  plugin_id: string;
  plugin_slug: string;
  values: IntegrationContextValues;
  /**
   * Resolved freeform overrides text. When multiple scopes have non-empty
   * overrides, they are concatenated under `### Organization-wide`,
   * `### Team`, and `### Agent-specific` sub-headings.
   */
  overrides: string;
}

/** Append-only audit row for changes to IntegrationContext.overrides. */
export interface IntegrationContextOverridesAuditEntry {
  id: string;
  agent_integration_context_id: string;
  changed_by: string | null;
  changed_at: string;
  before_value: string | null;
  after_value: string;
}

// ---------------------------------------------------------------------------
// Agent ↔ IntegrationDef binding
// ---------------------------------------------------------------------------

export interface AgentIntegrationInstall {
  id: string;
  agent_id: string;
  plugin_id: string;
  plugin_version: number;
  auto_upgrade: boolean;
  /** Subset of plugin.defined_scopes[].id. Empty array = all scopes (legacy/backward compat). */
  granted_scopes: string[];
  /** Skill IDs the user opted out of. Empty array = no exclusions, all skills deployed. */
  excluded_skill_ids: string[];
  installed_at: string;
  installed_by: string | null;
  upgraded_at: string | null;
}

// ---------------------------------------------------------------------------
// Team-level scope overrides
// ---------------------------------------------------------------------------

export interface IntegrationScopeOverride {
  id: string;
  team_id: string;
  plugin_id: string;
  /** References plugin.defined_scopes[].id */
  scope_id: string;
  /** Overridden minimum role required to grant this scope */
  min_role: TeamRole;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Scope approval requests
// ---------------------------------------------------------------------------

export type ScopeRequestStatus = 'pending' | 'approved' | 'denied' | 'expired';

export interface IntegrationScopeRequest {
  id: string;
  team_id: string;
  agent_id: string;
  plugin_id: string;
  /** Scope IDs being requested */
  requested_scopes: string[];
  reason: string | null;
  status: ScopeRequestStatus;
  requested_by: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  expires_at: string | null;
  created_at: string;
}
